(() => {
  if (window.solevureAtelierInitialized) return;
  window.solevureAtelierInitialized = true;
  const storefrontRoot = () => window.Shopify?.routes?.root || '/';
  const galleries = new WeakMap();
  const initGalleries = () => document.querySelectorAll('[data-at-gallery]').forEach(gallery => {
    if (galleries.has(gallery)) return;
    const controller = new AbortController();
    const signal = controller.signal;
    const slides = gallery.querySelectorAll('.at-card-slide');
    const progress = gallery.parentElement.querySelector('.at-card-progress span');
    const updateProgress = () => {
      if (!gallery.clientWidth || !slides.length) return;
      const index = Math.max(0, Math.min(slides.length - 1, Math.round(gallery.scrollLeft / gallery.clientWidth)));
      if (progress) progress.style.transform = `translateX(${index * 100}%)`;
    };
    gallery.addEventListener('scroll', updateProgress, { passive: true, signal });
    gallery.addEventListener('keydown', event => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const current = Math.round(gallery.scrollLeft / gallery.clientWidth);
      const index = event.key === 'Home' ? 0 : event.key === 'End' ? slides.length - 1 : current + (event.key === 'ArrowRight' ? 1 : -1);
      gallery.scrollTo({ left: Math.max(0, Math.min(slides.length - 1, index)) * gallery.clientWidth, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }, { signal });
    let startX = 0;
    let startY = 0;
    let moved = false;
    gallery.addEventListener('pointerdown', event => { startX = event.clientX; startY = event.clientY; moved = false; }, { passive: true, signal });
    gallery.addEventListener('pointermove', event => { if (Math.abs(event.clientX - startX) > 8 || Math.abs(event.clientY - startY) > 8) moved = true; }, { passive: true, signal });
    gallery.addEventListener('click', event => { if (moved && event.detail !== 0) { event.preventDefault(); event.stopPropagation(); moved = false; } }, { capture: true, signal });
    const observer = new ResizeObserver(updateProgress);
    observer.observe(gallery);
    galleries.set(gallery, { controller, observer });
  });
  const shelfRequests = new WeakMap();
  const initRecommendations = () => document.querySelectorAll('[data-at-recommendations]').forEach(async section => {
    if (shelfRequests.has(section)) return;
    const controller = new AbortController();
    shelfRequests.set(section, controller);
    try {
      const response = await fetch(section.dataset.url, { signal: controller.signal });
      if (!response.ok) return;
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = html.querySelector('[data-at-recommendations]');
      if (!controller.signal.aborted && section.isConnected && content?.querySelector('.at-shelf-card')) { section.replaceChildren(...content.childNodes); section.hidden = false; }
    } catch {}
  });
  const initHistory = () => document.querySelectorAll('[data-at-history]').forEach(async section => {
    if (shelfRequests.has(section)) return;
    const track = section.querySelector('.at-shelf-track');
    if (!track) return;
    const controller = new AbortController();
    shelfRequests.set(section, controller);
    const storageKey = 'solevure-recently-viewed';
    const current = section.dataset.currentHandle;
    let handles = [];
    try {
      const stored = JSON.parse(localStorage.getItem(storageKey) || '[]');
      if (Array.isArray(stored)) handles = stored.filter(handle => typeof handle === 'string' && /^[a-z0-9-]+$/.test(handle));
      handles = [...new Set(handles)].filter(handle => handle !== current).slice(0, 10);
      if (current) localStorage.setItem(storageKey, JSON.stringify([current, ...handles].slice(0, 11)));
    } catch {}
    const cards = await Promise.all(handles.map(async handle => {
      try {
        const response = await fetch(`${storefrontRoot()}products/${encodeURIComponent(handle)}?section_id=atelier-history-card`, { signal: controller.signal });
        if (!response.ok) return null;
        return new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('[data-at-history-card]');
      } catch { return null; }
    }));
    if (controller.signal.aborted || !section.isConnected) return;
    track.replaceChildren(...cards.filter(Boolean));
    section.hidden = !track.children.length;
  });
  const key = 'solevure-saved';
  let savedStorageAvailable = true;
  const savedProductPath = url => typeof url === 'string' ? url.match(/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(products\/[a-z0-9-]+)(?:\?.*)?$/i)?.[1] : undefined;
  const readSaved = () => {
    try {
      const stored = JSON.parse(localStorage.getItem(key) || '[]');
      if (!Array.isArray(stored)) return [];
      const products = new Map();
      stored.forEach(item => {
        const path = savedProductPath(item?.url);
        if (path && !products.has(path)) products.set(path, { url: item.url, title: typeof item.title === 'string' ? item.title : '', image: typeof item.image === 'string' ? item.image : '' });
      });
      return Array.from(products.values());
    } catch { savedStorageAvailable = false; return null; }
  };
  let saved = readSaved() || [];
  const update = () => {
    document.querySelectorAll('[data-at-saved-count]').forEach(element => {
      element.textContent = String(saved.length);
      if (element.hasAttribute('data-at-saved-hide-empty')) element.hidden = saved.length === 0;
    });
    document.querySelectorAll('[data-at-saved-label]').forEach(element => element.setAttribute('aria-label', `${element.dataset.atSavedLabel} (${saved.length})`));
    document.querySelectorAll('[data-at-saved-note]').forEach(element => element.textContent = savedStorageAvailable ? element.dataset.persistentLabel : element.dataset.sessionLabel);
    document.querySelectorAll('[data-at-save]').forEach(button => button.setAttribute('aria-pressed', String(saved.some(item => savedProductPath(item.url) === savedProductPath(button.dataset.url)))));
    const grid = document.querySelector('[data-at-saved-grid]');
    if (!grid) return;
    grid.replaceChildren();
    const empty = document.querySelector('[data-at-saved-empty]');
    if (empty) empty.hidden = saved.length > 0;
    saved.forEach((item, index) => {
      const path = savedProductPath(item.url);
      if (!path) return;
      const card = document.createElement('article');
      const link = document.createElement('a');
      link.href = `${storefrontRoot()}${path}${new URL(item.url, window.location.origin).search}`;
      if (/^(https:\/\/cdn\.shopify\.com\/|\/\/solevure\.com\/|https:\/\/solevure\.com\/|\/\/cdn\.shopify\.com\/)/.test(item.image)) {
        const image = document.createElement('img');
        image.src = item.image;
        image.alt = '';
        image.loading = 'lazy';
        image.decoding = 'async';
        link.append(image);
      }
      link.append(document.createTextNode(item.title));
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.textContent = '×';
      remove.setAttribute('aria-label', `${grid.dataset.removeLabel} ${item.title}`);
      remove.addEventListener('click', () => {
        saved = saved.filter(row => savedProductPath(row.url) !== path);
        persist();
        const next = grid.querySelectorAll('button');
        (next[Math.min(index, next.length - 1)] || grid.closest('dialog')?.querySelector('[data-at-close]'))?.focus({ preventScroll: true });
      });
      card.append(link, remove); grid.append(card);
    });
  };
  const persist = () => {
    try { localStorage.setItem(key, JSON.stringify(saved)); savedStorageAvailable = true; }
    catch { savedStorageAvailable = false; }
    update();
  };
  window.addEventListener('storage', event => {
    if (event.key !== key && event.key !== null) return;
    saved = readSaved() || saved;
    update();
  });
  document.addEventListener('change', event => {
    if (event.target instanceof Element && event.target.matches('.at-footer-region select')) event.target.form?.requestSubmit();
  });
  document.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const open = event.target.closest('[data-at-open]');
    if (open) {
      const dialog = document.getElementById(open.dataset.atOpen);
      if (dialog instanceof HTMLDialogElement) {
        event.preventDefault();
        const opener = open.closest('dialog')?.atOpener || open;
        document.querySelectorAll('.at-dialog[open]').forEach(active => { if (active !== dialog) active.close(); });
        update();
        dialog.atOpener = opener;
        if (!dialog.open) dialog.showModal();
        if (dialog.id === 'at-search') dialog.querySelector('input[type="search"]')?.focus();
      }
    }
    const close = event.target.closest('[data-at-close]');
    if (close) close.closest('dialog')?.close();
    const save = event.target.closest('[data-at-save]');
    if (save) {
      const path = savedProductPath(save.dataset.url);
      if (path) {
        const exists = saved.some(item => savedProductPath(item.url) === path);
        saved = exists ? saved.filter(item => savedProductPath(item.url) !== path) : [...saved, { url: save.dataset.url, title: save.dataset.title || '', image: save.dataset.image || '' }];
        persist();
      }
    }
    if (event.target.matches('.at-dialog')) {
      const rect = event.target.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) event.target.close();
    }
    document.querySelectorAll('.at-nav-group[open]').forEach(group => { if (!group.contains(event.target)) group.open = false; });
  });
  document.addEventListener('toggle', event => { if (event.target instanceof Element && event.target.matches('.at-nav-group') && event.target.open) document.querySelectorAll('.at-nav-group[open]').forEach(group => { if (group !== event.target) group.open = false; }); }, true);
  document.addEventListener('keydown', event => { if (event.key === 'Escape') document.querySelectorAll('.at-nav-group[open]').forEach(group => { group.open = false; group.querySelector('summary')?.focus(); }); });
  document.addEventListener('close', event => { const dialog = event.target; if (dialog.matches?.('.at-dialog') && !document.querySelector('.at-dialog[open]')) dialog.atOpener?.focus({preventScroll:true}); }, true);
  const updateCartCount = count => {
    if (!Number.isInteger(count) || count < 0) return;
    document.querySelectorAll('[data-at-cart-count]').forEach(el => el.textContent = count);
    document.querySelectorAll('[data-at-cart-label]').forEach(el => el.setAttribute('aria-label', `${el.dataset.atCartLabel} (${count})`));
  };
  let cartRefreshController;
  const refreshCartCount = async () => {
    cartRefreshController?.abort();
    const controller = new AbortController();
    cartRefreshController = controller;
    try {
      const response = await fetch(`${storefrontRoot()}cart.js`, { signal: controller.signal });
      if (!response.ok) return;
      const cart = await response.json();
      if (!controller.signal.aborted) updateCartCount(cart.item_count);
    } catch {}
  };
  document.addEventListener('cart:update', refreshCartCount);
  import('@shopify/events').then(({ StandardEvents }) => {
    document.addEventListener(StandardEvents.productSelect, event => event.promise?.then(update).catch(() => {}));
    document.addEventListener(StandardEvents.cartLinesUpdate, event => {
      event.promise?.then(({ cart }) => {
        if (!Number.isInteger(cart?.totalQuantity)) { refreshCartCount(); return; }
        cartRefreshController?.abort();
        updateCartCount(cart.totalQuantity);
      }).catch(() => {});
    });
  }).catch(() => {});
  const initialize = () => {
    initGalleries();
    initRecommendations();
    initHistory();
    update();
  };
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('shopify:section:unload', event => {
    if (!(event.target instanceof Element)) return;
    event.target.querySelectorAll('[data-at-gallery]').forEach(gallery => {
      const state = galleries.get(gallery);
      state?.controller.abort();
      state?.observer.disconnect();
      galleries.delete(gallery);
    });
    event.target.querySelectorAll('[data-at-history],[data-at-recommendations]').forEach(section => {
      shelfRequests.get(section)?.abort();
      shelfRequests.delete(section);
    });
  });
  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    saved = readSaved() || saved;
    update();
    refreshCartCount();
  });
  initialize();
})();

